import * as React from 'react'
import {
  AlertTriangle,
  BookmarkPlus,
  Check,
  MoreHorizontal,
  Play,
  RotateCw,
  Square,
} from 'lucide-react'
import type { ResumeRosterDto, ResumeSource, ResumeStateDto } from '@acs/shared'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useCancelResume, useClearRoster, useResume, useSaveRoster, useStartResume } from '@/hooks/use-resume'
import { useRelativeTime } from '@/hooks/use-relative-time'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dropdown, type DropdownItem } from '@/components/ui/dropdown'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/toast'

/**
 * The resume bar — save who is running, and start them again after a deploy.
 *
 * ── The problem it solves ───────────────────────────────────────────
 * A deploy restarts the API, and run state is deliberately not persisted, so
 * every running customer stops at once with nothing on screen to say who they
 * were. Rebuilding that list by hand, across a paginated table, is the job this
 * removes.
 *
 * ── Why "Save all running" is a SERVER-side resolve ─────────────────
 * The table's row selection is per page and is cleared on every page change, so
 * a client-side "select all" can only ever capture the fifty rows in front of
 * the operator — and the fleet is larger than that. This button sends no ids at
 * all: it asks the server to resolve "everyone running" from its own run
 * registry, which is the same set the Sessions filter is derived from. That is
 * the only way the promise on the button can be true.
 *
 * The bar shows itself only when there is something to say: a saved roster, a
 * snapshot to fall back on, or a run in progress.
 */

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'The request did not reach the server.'
}

/** Epoch ms → the ISO string `useRelativeTime` reads. */
function useAgo(savedAt: number | null): string {
  const iso = React.useMemo(
    () => (savedAt === null || savedAt <= 0 ? null : new Date(savedAt).toISOString()),
    [savedAt]
  )
  return useRelativeTime(iso)
}

function RosterChip(p: { roster: ResumeRosterDto; label: string; tone: 'info' | 'neutral' }) {
  const ago = useAgo(p.roster.savedAt)
  return (
    <Tooltip content={`Saved by ${p.roster.savedBy}`}>
      <span className="flex cursor-default items-center gap-1.5">
        <Badge tone={p.tone}>{p.label}</Badge>
        <span className="tabular text-[12px] text-fg">{p.roster.customerIds.length}</span>
        <span className="text-[11.5px] text-faint">{ago}</span>
      </span>
    </Tooltip>
  )
}

/* ------------------------------------------------------------------ *
 * While a resume is running
 * ------------------------------------------------------------------ */

function RunningView(p: { run: ResumeStateDto; onCancel: () => void; cancelling: boolean }) {
  const { run } = p
  const pct = run.total === 0 ? 0 : Math.round((run.done / run.total) * 100)
  const failed = run.counts.failed

  return (
    <>
      <Badge tone="live" dot pulse>
        {run.phase === 'cancelling' ? 'Stopping' : 'Resuming'}
      </Badge>

      <span className="tabular font-mono text-[12px] text-fg">
        {run.done}/{run.total}
      </span>

      {/* Shape as well as number: a bar is read at a glance from across a desk,
          which is how this is actually watched. */}
      <div
        className="hidden h-1 w-32 flex-none overflow-hidden rounded-full bg-s3 sm:block"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Resume progress"
      >
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      {run.current !== null ? (
        <span className="hidden min-w-0 truncate font-mono text-[11.5px] text-muted md:block">
          {run.current}
        </span>
      ) : null}

      {failed > 0 ? (
        <Badge tone="danger">
          {failed} failed
        </Badge>
      ) : null}

      <span className="ml-auto flex items-center gap-2">
        <span className="hidden text-[11px] text-faint lg:block">
          one every {run.staggerMs}ms
        </span>
        <Tooltip content="Stops the remaining starts. Customers already started keep running.">
          <Button variant="danger" size="xs" onClick={p.onCancel} disabled={p.cancelling}>
            <Square aria-hidden="true" />
            {p.cancelling ? 'Stopping…' : 'Cancel'}
          </Button>
        </Tooltip>
      </span>
    </>
  )
}

/* ------------------------------------------------------------------ *
 * After it finishes
 * ------------------------------------------------------------------ */

function SummaryView(p: { run: ResumeStateDto; onDismiss: () => void }) {
  const { run } = p
  const skipped =
    run.counts.skipped_running + run.counts.skipped_gender + run.counts.skipped_missing
  const failedIds = run.items.filter((i) => i.outcome === 'failed').map((i) => i.customerId)
  const clean = skipped === 0 && run.counts.failed === 0

  return (
    <>
      {clean ? (
        <Badge tone="ok">
          <Check aria-hidden="true" className="size-3" strokeWidth={3} />
          {run.phase === 'cancelled' ? 'Cancelled' : 'Resumed'}
        </Badge>
      ) : (
        <Badge tone={run.counts.failed > 0 ? 'danger' : 'warn'}>
          {run.phase === 'cancelled' ? 'Cancelled' : 'Resumed'}
        </Badge>
      )}

      <span className="min-w-0 text-[12.5px] text-fg">
        <strong className="tabular font-medium">{run.counts.started}</strong> started
        {skipped > 0 ? (
          <>
            {' · '}
            <span className="text-muted">{skipped} skipped</span>
          </>
        ) : null}
        {run.counts.failed > 0 ? (
          <>
            {' · '}
            <span className="text-danger">{run.counts.failed} failed</span>
          </>
        ) : null}
      </span>

      {/* The one skip an operator has to DO something about — the server cannot
          ask for a gender, so those customers are simply still stopped. */}
      {run.counts.skipped_gender > 0 ? (
        <span className="hidden text-[11.5px] text-warn md:block">
          {run.counts.skipped_gender} need a gender on file
        </span>
      ) : null}

      {failedIds.length > 0 ? (
        <Tooltip content={failedIds.join(', ')}>
          <span className="hidden cursor-default truncate font-mono text-[11px] text-danger lg:block">
            {failedIds.slice(0, 3).join(', ')}
            {failedIds.length > 3 ? ` +${failedIds.length - 3}` : ''}
          </span>
        </Tooltip>
      ) : null}

      {run.itemsOmitted > 0 ? (
        <span className="hidden text-[11px] text-faint lg:block">
          +{run.itemsOmitted} more not listed
        </span>
      ) : null}

      <Button variant="ghost" size="xs" className="ml-auto" onClick={p.onDismiss}>
        Dismiss
      </Button>
    </>
  )
}

/* ------------------------------------------------------------------ *
 * The bar
 * ------------------------------------------------------------------ */

export function ResumeBar() {
  const { data } = useResume()
  const { toast } = useToast()

  const save = useSaveRoster()
  const start = useStartResume()
  const cancel = useCancelResume()
  const clear = useClearRoster()

  /*
   * Which finished run has been dismissed, keyed by its id — so dismissing one
   * summary does not hide the NEXT run's, and a reload does not resurrect the
   * one just dismissed for as long as the page is open.
   */
  const [dismissedRunId, setDismissedRunId] = React.useState<string | null>(null)

  /*
   * How many customers "Save all running" would capture — the SERVER's own
   * count, from the same function that performs the save.
   *
   * Every attempt to derive this in the browser was a different expression over
   * a different snapshot, and each drifted by a customer or two in whichever
   * direction its staleness fell: the run map is seeded once per page load with
   * a `live` flag frozen at publish time, and `stats.live` counts every status
   * rather than the running set the save walks. A button that names a count has
   * to be exactly right or it should not name one, so the number now arrives on
   * the wire from the process that will act on it.
   */
  const runningCount = data?.runningNow ?? 0

  const run = data?.run
  const manual = data?.manual ?? null
  const auto = data?.auto ?? null

  const busy = run?.phase === 'running' || run?.phase === 'cancelling'
  const finished =
    (run?.phase === 'done' || run?.phase === 'cancelled') &&
    run.runId !== null &&
    run.runId !== dismissedRunId

  const onSaveAllRunning = (): void => {
    save.mutate(
      { allRunning: true },
      {
        onSuccess: (res) =>
          toast({
            title: `Saved ${res.saved} for resume`,
            description:
              'Deploy when you are ready, then press "Start saved" to bring them back one at a time.',
            tone: 'ok',
          }),
        onError: (err) =>
          toast({ title: 'Roster not saved', description: errorText(err), tone: 'danger' }),
      }
    )
  }

  const onStart = (source: ResumeSource): void => {
    start.mutate(source, {
      onSuccess: (res) => {
        setDismissedRunId(null)
        toast({
          title: `Starting ${res.total} ${res.total === 1 ? 'customer' : 'customers'}`,
          description: 'They are being started one at a time — watch the rows come up.',
          tone: 'ok',
        })
      },
      onError: (err) =>
        toast({ title: 'Could not start the resume', description: errorText(err), tone: 'danger' }),
    })
  }

  const onClear = (source: ResumeSource): void => {
    clear.mutate(source, {
      onSuccess: () =>
        toast({
          title: source === 'manual' ? 'Saved roster cleared' : 'Snapshot cleared',
          tone: 'neutral',
        }),
      onError: (err) =>
        toast({ title: 'Could not clear it', description: errorText(err), tone: 'danger' }),
    })
  }

  /*
   * The bar shows itself whenever it has something to offer — and "something to
   * offer" INCLUDES a fleet that is running but has never been saved.
   *
   * That last case is the one that matters: hiding until a roster exists would
   * mean the button that creates the first roster is only visible once a roster
   * exists, and an operator who has never saved could never find it. So a
   * running fleet is enough to bring it out, and the only state it stays out of
   * is the genuinely empty one — nothing running, nothing saved, nothing to
   * report.
   */
  if (!run) return null
  if (!busy && !finished && manual === null && auto === null && runningCount === 0) return null

  const overflow: DropdownItem[] = []
  if (auto !== null && !busy) {
    overflow.push({
      key: 'start-auto',
      label: `Start last snapshot (${auto.customerIds.length})`,
      icon: <RotateCw aria-hidden="true" />,
      onSelect: () => onStart('auto'),
    })
  }
  if (manual !== null) {
    overflow.push({
      key: 'clear-manual',
      label: 'Clear saved roster',
      danger: true,
      separatorBefore: overflow.length > 0,
      onSelect: () => onClear('manual'),
    })
  }
  if (auto !== null) {
    overflow.push({
      key: 'clear-auto',
      label: 'Clear snapshot',
      danger: true,
      separatorBefore: overflow.length > 0 && manual === null,
      onSelect: () => onClear('auto'),
    })
  }

  return (
    <div
      className={cn(
        'flex flex-none flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-3 py-2 sm:px-4',
        // A running resume is the only thing on this screen doing work nobody
        // asked for row by row, so it gets a tint. Idle, it stays quiet.
        busy ? 'bg-accent-soft' : 'bg-s2'
      )}
      role="region"
      aria-label="Resume roster"
    >
      {busy ? (
        <RunningView
          run={run}
          cancelling={run.phase === 'cancelling' || cancel.isPending}
          onCancel={() => cancel.mutate()}
        />
      ) : finished ? (
        <SummaryView run={run} onDismiss={() => setDismissedRunId(run.runId)} />
      ) : (
        <>
          <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-fg">
            <BookmarkPlus aria-hidden="true" className="size-3.5 text-accent" />
            Resume roster
          </span>

          {manual !== null ? (
            <RosterChip roster={manual} label="Saved" tone="info" />
          ) : (
            <span className="text-[12px] text-faint">
              Nothing saved yet
              {auto !== null ? ' — the last snapshot is the fallback' : ''}
            </span>
          )}
          {auto !== null && manual === null ? (
            <RosterChip roster={auto} label="Snapshot" tone="neutral" />
          ) : null}

          <span className="ml-auto flex flex-wrap items-center gap-2">
            {manual !== null ? (
              <Tooltip
                content={`Starts each saved customer on the server, one every ${run.staggerMs}ms. Ones already running are skipped.`}
              >
                <Button
                  variant="success"
                  size="xs"
                  onClick={() => onStart('manual')}
                  loading={start.isPending}
                >
                  <Play aria-hidden="true" />
                  Start saved ({manual.customerIds.length})
                </Button>
              </Tooltip>
            ) : auto !== null ? (
              <Tooltip content="Starts the customers that were running when the server last stopped.">
                <Button
                  variant="success"
                  size="xs"
                  onClick={() => onStart('auto')}
                  loading={start.isPending}
                >
                  <Play aria-hidden="true" />
                  Start snapshot ({auto.customerIds.length})
                </Button>
              </Tooltip>
            ) : null}

            {/* The button the fleet-scale save actually goes through — the count
                is every running customer, not the current page's selection. */}
            <Tooltip
              content={
                runningCount === 0
                  ? 'No approved customer is running, so there is nothing to save.'
                  : 'Saves every approved customer that is running, across every page. Replaces whatever is saved now.'
              }
            >
              <span className="inline-block">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={onSaveAllRunning}
                  loading={save.isPending}
                  disabled={runningCount === 0}
                >
                  <BookmarkPlus aria-hidden="true" />
                  Save all running ({runningCount})
                </Button>
              </span>
            </Tooltip>

            {overflow.length > 0 ? (
              <Dropdown
                align="end"
                label="More resume actions"
                trigger={
                  <Button variant="ghost" size="xs" iconOnly aria-label="More resume actions">
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                }
                items={overflow}
              />
            ) : null}
          </span>
        </>
      )}

      {/* A stale roster is worse than none: it starts customers who have since
          been stopped on purpose. Said once, quietly, and only when it applies. */}
      {!busy && !finished && manual !== null && runningCount > manual.customerIds.length ? (
        <span className="flex w-full items-center gap-1.5 text-[11.5px] text-warn">
          <AlertTriangle aria-hidden="true" className="size-3.5 flex-none" />
          {runningCount - manual.customerIds.length} more{' '}
          {runningCount - manual.customerIds.length === 1 ? 'customer is' : 'customers are'} running
          than the saved roster holds. Save again to include them.
        </span>
      ) : null}
    </div>
  )
}
